import { action, observable } from "mobx";
import IsdsResponse from "models/isdsResponse";
import Omission from "models/omission";
import OmissionRepository from "repositories/omissionRepository";
import EshopError from "../models/eshopError";
import EventBus, {
  cancellationApplicationProduction,
  omissionCreationFailed,
  omissionDocumentsUploadFailed,
  omissionDocumentsUploadSuccess,
} from "./eventBus";
import { Notice } from "./NIA/niaService";
import { PersistedBase64 } from "../models/notice";
import { plainToClass } from "class-transformer";
import * as Sentry from "@sentry/react";
import ExemptionCancellationApplicationProductionResponse from "entities/exemptionCancellationApplicationProductionResponse";
import ChargeRegistrationService from "./chargeRegistrationService";

export const SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY = "SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY";
export const POST_OMISSION_CREATION_FAILED = "POST_OMISSION_CREATION_FAILED";
export const POST_OMISSION_DOCUMENTS_UPLOAD_FAILED = "POST_OMISSION_DOCUMENTS_UPLOAD_FAILED";

export default class OmissionService {
  @observable notice: Omission = new Omission();
  @observable cancellationApplicationProduction: ExemptionCancellationApplicationProductionResponse | undefined;

  constructor(
    private repository: OmissionRepository,
    private chargeRegistrationService: ChargeRegistrationService,
    private eventBus: EventBus
  ) {
    this.initialize();
  }

  @action.bound
  async createOmission(omission: Omission) {
    return await this.repository
      .createOmission(omission)
      .then(async (response: any | EshopError) => {
        if (response instanceof EshopError) {
          this.eventBus.publish(omissionCreationFailed({ type: response.type }));
        } else if (response.blob && response.fileName) {
          return response;
        }
      })
      .catch(() => {
        this.eventBus.publish(omissionCreationFailed({ type: POST_OMISSION_CREATION_FAILED }));
        return undefined;
      });
  }

  @action.bound
  async getProduction(omission: Omission) {
    return await this.repository
      .getProduction(omission)
      .then(response => {
        this.cancellationApplicationProduction = response as ExemptionCancellationApplicationProductionResponse;
        return response;
      })
      .catch(() => this.eventBus.publish(cancellationApplicationProduction({ cancellationApplicationProduction: undefined })));
  }

  @action.bound
  async uploadDocuments(ISDSSessionId: string, files: PersistedBase64[]) {
    sessionStorage.setItem(SESSION_STORAGE_ISDS_UPLOAD_REQUEST_SOURCE_KEY, Notice.OMISSION);
    return await this.repository
      .uploadDocuments(ISDSSessionId, files)
      .then(response => {
        if (response instanceof IsdsResponse) {
          this.eventBus.publish(omissionDocumentsUploadSuccess({ conceptIdent: response.conceptIdent }));
        } else {
          this.publishDocumentsUploadFailure(response.type, response, files, ISDSSessionId);
        }
      })
      .catch(error => {
        this.publishDocumentsUploadFailure(POST_OMISSION_DOCUMENTS_UPLOAD_FAILED, error, files, ISDSSessionId);
      });
  }

  private publishDocumentsUploadFailure(type: string, error: any, files: PersistedBase64[], sessionId: string) {
    Sentry.withScope(scope => {
      scope.setTag("OmissionService", "upload");
      scope.setExtras({
        type,
        files,
        sessionId,
      });
      Sentry.captureException(error);
    });

    this.eventBus.publish(omissionDocumentsUploadFailed({ type }));
  }

  @action.bound
  initialize() {
    const noticeString = sessionStorage.getItem("omission");
    this.notice = new Omission();

    if (noticeString) {
      this.notice = plainToClass(Omission, JSON.parse(noticeString));
    }
  }

  @action.bound
  reset = () => {
    this.notice = new Omission();
    this.chargeRegistrationService.vehicleChargingInstructions = undefined;
    this.cancellationApplicationProduction = undefined;
  };
}
